import React, { useState, useCallback, useEffect } from 'react';
import { Card, Button, Table, Tag, Space, Input } from 'antd';
import { CommentOutlined, MessageOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { Service } from '@/apis';
import { DataStats, type DataStat } from '@/components/CommonTable';
import { createTimeColumn, createLongTextColumn } from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

type SessionRow = Record<string, unknown> & {
  id?: number | string;
  sessionId: string;
  lastMessage?: string;
  lastTime?: number | string;
  unreadCount?: number;
  createTime?: number | string;
};

const PAGE_SIZE = 20;

/** 后端可能返回 data.list + data.total，且 lastMessage 为对象 { content, from } */
const normalizeRows = (rows: unknown[]): SessionRow[] => {
  return (rows || []).map((r: unknown) => {
    const item = r as Record<string, unknown>;
    const data = (item.data as Record<string, unknown>) || item;
    const sid = (data.sessionId as string) ?? (item.sessionId as string) ?? '';
    const lastMsg = (data.lastMessage as Record<string, unknown>) ?? (item.lastMessage as Record<string, unknown>);
    const lastMessageStr =
      typeof lastMsg === 'string'
        ? lastMsg
        : (lastMsg?.content as string) ?? '';
    const lastTime = (data.lastTime as number) ?? (item.lastTime as number) ?? (data.createTime as number) ?? (item.createTime as number);
    return {
      ...item,
      ...data,
      sessionId: sid,
      lastMessage: lastMessageStr,
      lastTime,
      createTime: lastTime || (data.createTime as number) || (item.createTime as number),
      unreadCount: (data.unreadCount as number) ?? (item.unreadCount as number) ?? 0,
    } as SessionRow;
  });
};

const ChatManagement: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pn, setPn] = useState(1);
  const [total, setTotal] = useState(0);
  const [sessionIdFilter, setSessionIdFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const fetchSessions = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await Service.ycChatMessageControllerGetSessions(
          page,
          PAGE_SIZE,
          sessionIdFilter || undefined
        );
        const raw = (res as { data?: { rows?: unknown[]; list?: unknown[]; count?: number; total?: number; pn?: number } })?.data;
        const listOrRows = raw?.list ?? raw?.rows ?? [];
        const rows = normalizeRows(Array.isArray(listOrRows) ? listOrRows : []);
        let filtered = rows;
        if (unreadOnly) {
          filtered = rows.filter((r) => (r.unreadCount ?? 0) > 0);
        }
        setList(filtered);
        const totalNum = raw?.total ?? raw?.count ?? filtered.length;
        setTotal(totalNum);
        setPn(raw?.pn ?? page);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [sessionIdFilter, unreadOnly]
  );

  // 进入列表或筛选变化时拉取；从会话详情返回时组件会重新挂载，会再次拉取最新未读状态
  useEffect(() => {
    fetchSessions(1);
  }, [sessionIdFilter, unreadOnly]);

  const handleTableChange = (page: number) => {
    setPn(page);
    fetchSessions(page);
  };

  const columns: ColumnsType<SessionRow> = [
    { title: '会话 ID', dataIndex: 'sessionId', key: 'sessionId', width: 200, ellipsis: true },
    createLongTextColumn('最新消息', 'lastMessage', 25),
    createTimeColumn('full'),
    {
      title: '未读',
      dataIndex: 'unreadCount',
      key: 'unreadCount',
      width: 80,
      render: (n: number) =>
        n > 0 ? <Tag color="red">{n}</Tag> : <span style={{ color: '#999' }}>0</span>,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/cms/chat/${encodeURIComponent(record.sessionId)}`)}
        >
          进入聊天
        </Button>
      ),
    },
  ];

  const stats: DataStat[] = [
    { title: '会话总数', value: total, prefix: <CommentOutlined />, valueStyle: { color: '#3f8600' } },
    {
      title: '有未读会话',
      value: list.filter((r) => (r.unreadCount ?? 0) > 0).length,
      prefix: <MessageOutlined />,
      valueStyle: { color: '#cf1322' },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        <DataStats stats={stats} className={styles.statsRow} />
        <div className={styles.toolbar}>
          <Space wrap>
            <Input
              placeholder="按会话 ID 搜索"
              value={sessionIdFilter}
              onChange={(e) => setSessionIdFilter(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
            <Button onClick={() => fetchSessions(1)} loading={loading}>
              查询
            </Button>
            <Button type={unreadOnly ? 'primary' : 'default'} onClick={() => setUnreadOnly(!unreadOnly)}>
              仅显示有未读
            </Button>
          </Space>
        </div>
        <Table<SessionRow>
          columns={columns}
          dataSource={list}
          loading={loading}
          rowKey="sessionId"
          pagination={{
            current: pn,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: handleTableChange,
          }}
        />
      </Card>
    </div>
  );
};

export default ChatManagement;
